// src/blocks/PeopleSpotlight/SpotlightGrid.tsx
'use client'

import React, { useEffect, useRef } from 'react'
import Link from 'next/link'

function slugifyName(name: string): string {
  return name
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

type SpotlightPerson = {
  id: string
  name: string
  role: string
  imageUrl?: string
}

export default function SpotlightGrid({ people }: { people: SpotlightPerson[] }) {
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const root = listRef.current
    if (!root) return
    const items = Array.from(root.querySelectorAll('[data-spotlight-card]')) as HTMLElement[]
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const el = e.target as HTMLElement
        if (e.isIntersecting) {
          el.classList.remove('opacity-0', 'translate-y-3')
          el.classList.add('opacity-100', 'translate-y-0')
          io.unobserve(el)
        }
      })
    }, { threshold: 0.2 })
    items.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])

  return (
    <ul
      ref={listRef}
      className="grid place-items-center grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-8 sm:gap-10 lg:gap-4"
    >
      {people.map((p, idx) => {
        const delay = 100 + idx * 200
        const href = `/team/${slugifyName(p.name)}`
        return (
          <li
            key={p.id}
            data-spotlight-card
            style={{ transitionDelay: `${delay}ms` }}
            className="flex flex-col items-center text-center opacity-0 translate-y-3 transition-all duration-700 ease-out will-change-transform motion-reduce:opacity-100 motion-reduce:translate-y-0 motion-reduce:transition-none"
          >
            <div className="mx-auto aspect-square w-full max-w-[18rem] sm:max-w-[14rem] md:max-w-[15rem] xl:max-w-[16rem] rounded-full overflow-hidden ring-2 ring-minigreen-7/50 shadow-sm">
              {p.imageUrl ? (
                <img src={p.imageUrl} alt={p.name ? `Portrait of ${p.name}` : 'Team member portrait'} className="h-full w-full object-cover" loading="lazy" decoding="async" sizes="(min-width: 1280px) 16rem, (min-width: 768px) 15rem, (min-width: 640px) 14rem, 18rem" />
              ) : (
                <div className="h-full w-full bg-neutral-200 dark:bg-neutral-800" />
              )}
            </div>

            <h3 className="mt-5 text-2xl sm:text-xl md:text-2xl xl:text-3xl font-semibold text-mini-primary dark:text-mini-dark-primary">
              {p.name}
            </h3>
            <p className="mt-1 italic text-base sm:text-sm md:text-base text-mini-secondary dark:text-mini-dark-secondary">{p.role}</p>

            <div className="mt-6 mb-12">
              <Link
                href={href}
                className="inline-flex items-center justify-center rounded-full px-5 py-2 text-sm md:text-base font-medium bg-minigreen-7 text-white hover:bg-minigreen-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-minigreen-7 transition-colors"
                aria-label={`Read bio for ${p.name}`}
              >
                Read Bio
              </Link>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
